import type { Ace } from 'ace-builds';
import { useCallback, useState } from 'react';
import jsonMap from 'json-source-map';
import { getValueAtPointer, JSONMap, SchemaNode } from '../lib/types';

type ObjectRange = {
  startRow: number;
  startColumn: number;
  endRow: number;
  endColumn: number;
};

const isDatapointObject = (value: unknown): boolean => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return (value as SchemaNode).category === 'datapoint';
};

const findDatapointRanges = (jsonText: string): ObjectRange[] => {
  try {
    const jsmap: JSONMap = jsonMap.parse(jsonText);
    const ranges: ObjectRange[] = [];

    Object.entries(jsmap.pointers).forEach(([pointer, location]) => {
      if (!location.value || !location.valueEnd) return;

      const value = getValueAtPointer(jsmap.data, pointer);

      if (isDatapointObject(value)) {
        const startRow = location.value.line;
        const endRow = location.valueEnd.line;

        if (endRow > startRow) {
          ranges.push({
            startRow,
            startColumn: location.value.column,
            endRow,
            endColumn: location.valueEnd.column,
          });
        }
      }
    });

    return ranges;
  } catch {
    return [];
  }
};

type EditSessionWithFolding = Ace.EditSession & {
  getFoldWidgetRange: (row: number) => Ace.Range | null;
  getFoldAt: (row: number, column: number, side?: number) => Ace.Fold | null;
};

export const useFoldObjects = (editor: Ace.Editor | null, jsonText: string) => {
  const [isFolded, setIsFolded] = useState(false);

  const foldAllObjects = useCallback(() => {
    if (!editor) return;

    const session = editor.getSession() as EditSessionWithFolding;
    const ranges = findDatapointRanges(jsonText);

    ranges
      .sort((a, b) => b.startRow - a.startRow)
      .forEach(range => {
        if (range.endRow > range.startRow) {
          try {
            const foldRange = session.getFoldWidgetRange(range.startRow);
            if (foldRange) {
              const existingFold = session.getFoldAt(
                foldRange.start.row,
                foldRange.start.column
              );
              if (!existingFold) {
                session.addFold('...', foldRange);
              }
            }
          } catch {
            // Fold already exists or invalid range
          }
        }
      });
    setIsFolded(true);
  }, [editor, jsonText]);

  const unfoldAll = useCallback(() => {
    if (!editor) return;

    const session = editor.getSession();
    session.unfold(null, true);
    setIsFolded(false);
  }, [editor]);

  const toggleFold = useCallback(() => {
    if (isFolded) {
      unfoldAll();
    } else {
      foldAllObjects();
    }
  }, [isFolded, unfoldAll, foldAllObjects]);

  return {
    foldAllObjects,
    unfoldAll,
    toggleFold,
    isFolded,
  };
};
